import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Home, ChevronRight, AlertCircle, FileEdit, FileText, Loader2, User, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ProcessFlowDiagram from '@/components/ProcessFlowDiagram';
import { formatDate } from '@/utils/formatters';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Schedule = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [worksOrders, setWorksOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [processData, setProcessData] = useState({});
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterProcess, setFilterProcess] = useState('');
  const [processOptions, setProcessOptions] = useState({});
  const [selectedProcessForEdit, setSelectedProcessForEdit] = useState(null);
  const [selectedProcessIndex, setSelectedProcessIndex] = useState(null);
  const [highlightedOrder, setHighlightedOrder] = useState(null);
  const [partDrawings, setPartDrawings] = useState([]);
  const [partDetails, setPartDetails] = useState(null);
  const [shortfallDialogOpen, setShortfallDialogOpen] = useState(false);
  const [shortfallQuantity, setShortfallQuantity] = useState(0);
  const [pendingShortfallOrder, setPendingShortfallOrder] = useState(null);

  useEffect(() => {
    fetchWorksOrders();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Handle search from landing page
    if (location.state?.searchOrder) {
      const orderNumber = location.state.searchOrder;
      setHighlightedOrder(orderNumber);
      
      // Find and open the order dialog
      const order = worksOrders.find(o => o.order_number === orderNumber);
      if (order) {
        handleOrderClick(order);
      }
      
      // Clear highlight after 3 seconds
      setTimeout(() => setHighlightedOrder(null), 3000);
      
      // Clear the navigation state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, worksOrders]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWorksOrders = async () => {
    try {
      const [ordersRes, processRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/works-orders`),
        axios.get(`${BACKEND_URL}/api/processes`)
      ]);
      setWorksOrders(ordersRes.data);
      setProcessOptions(processRes.data.process_options || {});
    } catch (error) {
      console.error('Error fetching works orders:', error);
      toast.error('Failed to load works orders');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setProcessData(order.process_data || {});
    setSelectedProcessForEdit(null);
    setSelectedProcessIndex(null);
    setDialogOpen(true);
    
    // Fetch part drawings
    fetchPartDrawings(order.part_number);
  };

  const fetchPartDrawings = async (partNumber) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/parts/${partNumber}`);
      setPartDrawings(response.data.drawings || []);
      setPartDetails(response.data);
    } catch (error) {
      console.error('Error fetching part drawings:', error);
      setPartDrawings([]);
      setPartDetails(null);
    }
  };

  const handleProcessFlowClick = (processName, processIndex) => {
    setSelectedProcessForEdit(processName);
    setSelectedProcessIndex(processIndex);
  };

  // Helper function to get status - supports both index-based and name-based formats
  const getProcessStatus = (order, index, processName) => {
    if (order.process_statuses[String(index)] !== undefined) {
      return order.process_statuses[String(index)];
    }
    return order.process_statuses[processName] || 'Not Ready';
  };

  const handleCompleteCurrentStep = async () => {
    if (!selectedOrder) return;
    
    // Find the current "In Progress" step by index
    let inProgressIndex = -1;
    for (let i = 0; i < selectedOrder.process_sequence.length; i++) {
      const status = getProcessStatus(selectedOrder, i, selectedOrder.process_sequence[i]);
      if (status === 'In Progress') {
        inProgressIndex = i;
        break;
      }
    }

    if (inProgressIndex === -1) {
      toast.error('No step is currently in progress');
      return;
    }

    const processName = selectedOrder.process_sequence[inProgressIndex];
    
    try {
      const updatedStatuses = { ...selectedOrder.process_statuses };
      updatedStatuses[String(inProgressIndex)] = 'Complete';
      
      // Auto-advance next step to Ready
      if (inProgressIndex + 1 < selectedOrder.process_sequence.length) {
        const nextIdx = String(inProgressIndex + 1);
        if (updatedStatuses[nextIdx] === 'Not Ready' || !updatedStatuses[nextIdx]) {
          updatedStatuses[nextIdx] = 'Ready';
        }
      }
      
      await axios.put(`${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}`, {
        process_statuses: updatedStatuses
      });
      
      toast.success(`${processName} completed for ${selectedOrder.order_number}`);
      setDialogOpen(false);
      
      // Update local state
      const response = await axios.get(`${BACKEND_URL}/api/works-orders`);
      setWorksOrders(response.data);
    } catch (error) {
      console.error('Error completing step:', error);
      toast.error('Failed to complete step');
    }
  };

  const handleProcessDataChange = (processName, field, value) => {
    // Use index as key to support duplicate process steps
    const key = String(selectedProcessIndex);
    setProcessData(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value
      }
    }));
  };

  const handleSaveProcessData = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/works-orders/${selectedOrder.order_number}`, {
        process_data: processData
      });
      toast.success('Process data saved successfully');
      
      // Check for shortfall if saving Final Inspection data
      if (selectedProcessForEdit === 'Final Inspection') {
        const finalInspectionData = processData[String(selectedProcessIndex)] || {};
        const inspectedQty = parseInt(finalInspectionData.quantity) || 0;
        const orderQty = selectedOrder.quantity || 0;
        
        if (inspectedQty > 0 && inspectedQty < orderQty) {
          const shortfall = orderQty - inspectedQty;
          setShortfallQuantity(shortfall);
          setPendingShortfallOrder(selectedOrder);
          setShortfallDialogOpen(true);
          setDialogOpen(false);
          return;
        }
      }
      
      setDialogOpen(false);
      
      // Update local state instead of full refresh to maintain scroll position
      const response = await axios.get(`${BACKEND_URL}/api/works-orders`);
      setWorksOrders(response.data);
    } catch (error) {
      console.error('Error saving process data:', error);
      toast.error('Failed to save process data');
    }
  };

  const handleCreateShortfallOrder = async () => {
    if (!pendingShortfallOrder) return;
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/works-orders`, {
        customer: pendingShortfallOrder.customer,
        customer_order_number: pendingShortfallOrder.customer_order_number ? `${pendingShortfallOrder.customer_order_number}-SF` : '',
        part_number: pendingShortfallOrder.part_number,
        quantity: shortfallQuantity,
        unit_price: pendingShortfallOrder.unit_price || 0,
        due_date: pendingShortfallOrder.due_date,
        notes: `Shortfall order from ${pendingShortfallOrder.order_number}. Original qty: ${pendingShortfallOrder.quantity}, Delivered: ${pendingShortfallOrder.quantity - shortfallQuantity}`,
        process_sequence: pendingShortfallOrder.process_sequence
      });
      
      toast.success(`Shortfall order ${response.data.order_number} created for ${shortfallQuantity} units`);
      setShortfallDialogOpen(false);
      setPendingShortfallOrder(null);
      setShortfallQuantity(0);
      
      // Refresh orders
      const ordersResponse = await axios.get(`${BACKEND_URL}/api/works-orders`);
      setWorksOrders(ordersResponse.data);
    } catch (error) {
      console.error('Error creating shortfall order:', error);
      toast.error('Failed to create shortfall order');
    }
  };

  const handleDeclineShortfallOrder = async () => {
    setShortfallDialogOpen(false);
    setPendingShortfallOrder(null);
    setShortfallQuantity(0);
    
    // Refresh orders
    const response = await axios.get(`${BACKEND_URL}/api/works-orders`);
    setWorksOrders(response.data);
  };

  // Calculate bars required based on part details and quantity
  const calculateBarsRequired = () => {
    if (!partDetails || !selectedOrder) return null;
    
    // Standard bar length is 5800mm
    const standardBarLength = 5800;
    const loc = partDetails.loc || 0;
    const quantity = selectedOrder.quantity || 0;
    
    if (loc <= 0) return quantity; // Fallback to quantity if no LOC
    
    // Pieces per bar = floor(5800 / (LOC + 2mm kerf allowance))
    const piecesPerBar = Math.floor(standardBarLength / (loc + 2));
    if (piecesPerBar <= 0) return quantity;
    
    const barsRequired = Math.ceil(quantity / piecesPerBar);
    
    return barsRequired;
  };

  const getProcessFields = (processName) => {
    const options = processOptions[processName];
    
    // Define fields for each process based on requirements
    switch (processName) {
      case 'Material Sourcing':
        return [
          { name: 'supplier', label: 'Supplier', type: 'text' },
          { name: 'po_number', label: 'Purchase Order Number', type: 'text' },
          { name: 'bars_ordered', label: 'Bars Ordered', type: 'number' }
        ];
      
      case 'Cutting':
        return [
          { name: 'cast_number', label: 'Material Cast Number', type: 'text' },
          { name: 'bundle_number', label: 'Bundle Number', type: 'text' },
          { name: 'notes', label: 'Notes', type: 'text' }
        ];
      
      case 'Face and Center':
        const fcFields = [];
        if (options?.options) {
          fcFields.push({
            name: 'equipment',
            label: 'Equipment',
            type: 'select',
            options: options.options
          });
        }
        fcFields.push({ name: 'operator', label: 'Operator Name', type: 'text' });
        return fcFields;
      
      case 'Forging':
        const forgingFields = [];
        if (options?.options) {
          forgingFields.push({
            name: 'equipment',
            label: 'Equipment',
            type: 'select',
            options: options.options
          });
        }
        forgingFields.push(
          { name: 'operator', label: 'Operator Name', type: 'text' },
          { name: 'quantity_scrap', label: 'Quantity Scrap', type: 'number' }
        );
        return forgingFields;
      
      case 'Machining':
        return [
          { name: 'machine_id', label: 'Machine ID', type: 'text' },
          { name: 'operator', label: 'Operator Name', type: 'text' },
          { name: 'cycle_time', label: 'Cycle Time', type: 'text' },
          { name: 'quantity_scrap', label: 'Quantity Scrap', type: 'number' },
          { name: 'notes', label: 'Notes', type: 'text' }
        ];
      
      case 'Milling':
        return [
          { name: 'machine_id', label: 'Machine ID', type: 'text' },
          { name: 'operator', label: 'Operator Name', type: 'text' },
          { name: 'cycle_time', label: 'Cycle Time', type: 'text' },
          { name: 'quantity_scrap', label: 'Quantity Scrap', type: 'number' },
          { name: 'notes', label: 'Notes', type: 'text' }
        ];
      
      case 'Heat Treatment':
        const htFields = [];
        if (options?.suppliers) {
          htFields.push({
            name: 'supplier',
            label: 'Supplier',
            type: 'select',
            options: options.suppliers
          });
        }
        if (options?.specifications) {
          htFields.push({
            name: 'specification',
            label: 'Specification',
            type: 'select',
            options: options.specifications
          });
        }
        htFields.push(
          { name: 'delivery_note_number', label: 'Delivery Note Number', type: 'text' },
          { name: 'ht_certificate_number', label: 'Heat Treatment Certificate Number', type: 'text' },
          { name: 'notes', label: 'Notes', type: 'text' }
        );
        return htFields;
      
      case 'Surface Coating':
        const scFields = [];
        if (options?.suppliers) {
          scFields.push({
            name: 'supplier',
            label: 'Supplier',
            type: 'select',
            options: options.suppliers
          });
        }
        if (options?.specifications) {
          scFields.push({
            name: 'specification',
            label: 'Specification',
            type: 'select',
            options: options.specifications
          });
        }
        scFields.push(
          { name: 'delivery_note_number', label: 'Delivery Note Number', type: 'text' },
          { name: 'sc_certificate_number', label: 'Surface Coating Certificate Number', type: 'text' },
          { name: 'notes', label: 'Notes', type: 'text' }
        );
        return scFields;
      
      case 'Other Subcontracted Services':
        return [
          { name: 'supplier', label: 'Supplier', type: 'text' },
          { name: 'delivery_note_number', label: 'Delivery Note Number', type: 'text' },
          { name: 'notes', label: 'Notes', type: 'text' }
        ];
      
      case 'Final Inspection':
        return [
          { name: 'quantity', label: 'Quantity', type: 'number' },
          { name: 'inspector_id', label: 'Inspector ID', type: 'text' },
          { name: 'other_scrap', label: 'Other Scrap', type: 'number' },
          { name: 'delivery_method', label: 'Delivery Method', type: 'text' },
          { name: 'notes', label: 'Notes', type: 'text' }
        ];
      
      case 'Invoice':
        return [
          { name: 'invoice_number', label: 'Invoice Number', type: 'text' },
          { name: 'delivery_note_number', label: 'Delivery Note Number', type: 'text' },
          { name: 'delivery_method', label: 'Delivery Method', type: 'text' },
          { name: 'invoice_date', label: 'Invoice Date', type: 'date' }
        ];
      
      default:
        return [{ name: 'notes', label: 'Notes', type: 'text' }];
    }
  };

  const filteredOrders = worksOrders.filter(order => {
    if (filterCustomer && !order.customer.toLowerCase().includes(filterCustomer.toLowerCase())) {
      return false;
    }
    if (filterProcess && !order.process_sequence.includes(filterProcess)) {
      return false;
    }
    return true;
  });

  const getUniqueCustomers = () => {
    return [...new Set(worksOrders.map(o => o.customer))];
  };

  const getUniqueProcesses = () => {
    const allProcesses = worksOrders.flatMap(o => o.process_sequence);
    return [...new Set(allProcesses)];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden text-white">
      {/* Honeycomb Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-schedule" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(20, 184, 166, 0.12)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(20, 184, 166, 0.12)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-schedule)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(20, 184, 166, 0.06) 0%, transparent 70%)' }} />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-teal-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-teal-400 hover:bg-teal-500/10" data-testid="back-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-500/30 flex items-center justify-center">
                <FileEdit className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide" data-testid="page-title">Schedule</h1>
                <p className="text-xs text-slate-500">Works Orders Overview</p>
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Home</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-teal-400 font-medium">Schedule</span>
          </nav>
          <div className="hidden sm:flex items-center gap-2 text-sm">
            <div className="w-8 h-8 rounded-full bg-teal-500/20 border border-teal-500/30 flex items-center justify-center">
              <User className="w-4 h-4 text-teal-400" />
            </div>
            <span className="text-slate-400">{user?.name || user?.email?.split('@')[0]}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[1600px] mx-auto">

        {/* Filters */}
        <div className="bg-slate-900/40 backdrop-blur-sm border border-teal-500/10 rounded-lg px-4 py-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Filter by Customer</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={filterCustomer}
                  onChange={(e) => setFilterCustomer(e.target.value)}
                  placeholder="Customer name..."
                  className="bg-slate-950/50 border-slate-800 pl-10"
                  data-testid="filter-customer"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Filter by Process</Label>
              <Select value={filterProcess || "all"} onValueChange={(val) => setFilterProcess(val === "all" ? "" : val)}>
                <SelectTrigger className="bg-slate-950/50 border-slate-800" data-testid="filter-process">
                  <SelectValue placeholder="All processes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All processes</SelectItem>
                  {getUniqueProcesses().map((proc) => (
                    <SelectItem key={proc} value={proc}>{proc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => { setFilterCustomer(''); setFilterProcess(''); }}
                className="border-slate-700 hover:border-slate-600"
                data-testid="clear-filters-btn"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Works Orders List */}
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-xl" data-testid="empty-state">
            <p className="text-slate-400 mb-4">No works orders found</p>
            <Button onClick={() => navigate('/works-order/create')} className="bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 border border-teal-500/50">
              Create Works Order
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4" data-testid="orders-list">
            {filteredOrders.map((order) => {
              // Find current process (first non-complete step)
              let currentProcessIdx = 0;
              for (let i = 0; i < order.process_sequence.length; i++) {
                const status = getProcessStatus(order, i, order.process_sequence[i]);
                if (status !== 'Complete') {
                  currentProcessIdx = i;
                  break;
                }
                if (i === order.process_sequence.length - 1) {
                  currentProcessIdx = i; // All complete
                }
              }
              const currentProcess = order.process_sequence[currentProcessIdx];
              const currentStatus = getProcessStatus(order, currentProcessIdx, currentProcess);
              
              // Count completed steps
              let completedCount = 0;
              for (let i = 0; i < order.process_sequence.length; i++) {
                const status = getProcessStatus(order, i, order.process_sequence[i]);
                if (status === 'Complete') completedCount++;
              }
              const totalSteps = order.process_sequence.length;
              const progress = (completedCount / totalSteps) * 100;

              return (
                <div
                  key={order.order_number}
                  className={`bg-slate-900/40 backdrop-blur-md border rounded-xl p-6 hover:border-teal-500/50 transition-all duration-300 hover:shadow-[0_0_20px_rgba(20,184,166,0.1)] cursor-pointer ${
                    highlightedOrder === order.order_number 
                      ? 'border-teal-400 shadow-[0_0_30px_rgba(20,184,166,0.4)] animate-pulse' 
                      : 'border-slate-800'
                  }`}
                  onClick={() => handleOrderClick(order)}
                  data-testid={`order-card-${order.order_number}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-mono text-white font-bold text-lg" data-testid="order-number">
                          {order.order_number}
                        </h3>
                        {order.shortage_flagged && (
                          <div className="flex items-center gap-1 text-red-400" data-testid="shortage-indicator">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-xs uppercase">Shortage</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Part:</span>
                          <p className="text-white font-mono">{order.part_number}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Customer:</span>
                          <p className="text-white">{order.customer}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Quantity:</span>
                          <p className="text-white">{order.quantity}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Due:</span>
                          <p className="text-white">{formatDate(order.due_date)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOrderClick(order);
                      }}
                      data-testid="edit-data-btn"
                      className="border-slate-700 hover:border-teal-500/50 text-slate-400 hover:text-teal-400"
                    >
                      <FileEdit className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-500">Progress: {completedCount}/{totalSteps} steps</span>
                      <span className="text-teal-400 font-bold">{Math.round(progress)}%</span>
                    </div>
                    <div className="w-full bg-slate-800 rounded-full h-2">
                      <div
                        className="bg-teal-400 h-2 rounded-full transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Current Process */}
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wider">Current Process:</span>
                    <p className="text-white font-bold">{currentProcess} - {currentStatus}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Data Input Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-slate-950 border-teal-500/30 max-w-5xl" data-testid="process-data-dialog">
            <DialogHeader>
              <DialogTitle className="text-white uppercase tracking-tight text-xl">
                {selectedOrder?.order_number} - Process Data
              </DialogTitle>
            </DialogHeader>

            {selectedOrder && (
              <div className="space-y-6">
                {/* Order Summary Section */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4">
                  <h4 className="text-white font-bold uppercase tracking-wide mb-3 text-sm border-b border-slate-800 pb-2">
                    Order Summary
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider">Part Number</span>
                      <p className="text-white font-mono font-bold">{selectedOrder.part_number}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider">Customer</span>
                      <p className="text-white font-bold">{selectedOrder.customer}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider">Order Quantity</span>
                      <p className="text-teal-400 font-mono font-bold text-lg">{selectedOrder.quantity}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wider">Due Date</span>
                      <p className="text-white font-bold">{formatDate(selectedOrder.due_date)}</p>
                    </div>
                  </div>
                </div>

                {/* Part Details Section */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4" data-testid="part-details-section">
                  <h4 className="text-blue-400 font-bold uppercase tracking-wide mb-3 text-sm border-b border-blue-500/20 pb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Part Details (Engineering Specification)
                  </h4>
                  {partDetails ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div className="col-span-2 md:col-span-3">
                        <span className="text-slate-500 text-xs uppercase tracking-wider">Part Description</span>
                        <p className="text-white font-bold" data-testid="part-description">{partDetails.description || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-xs uppercase tracking-wider">Material Grade</span>
                        <p className="text-white font-mono font-bold text-lg" data-testid="part-material-grade">{partDetails.material_grade || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-xs uppercase tracking-wider">Diameter</span>
                        <p className="text-white font-mono font-bold text-lg" data-testid="part-diameter">
                          {partDetails.diameter ? `${partDetails.diameter} mm` : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-xs uppercase tracking-wider">Length of Cut (LOC)</span>
                        <p className="text-white font-mono font-bold text-lg" data-testid="part-loc">
                          {partDetails.loc ? `${partDetails.loc} mm` : 'N/A'}
                        </p>
                      </div>
                      <div className="col-span-2 md:col-span-3 bg-slate-900/60 p-3 rounded-lg border border-slate-800">
                        <span className="text-slate-500 text-xs uppercase tracking-wider">Heat Treatment Requirement</span>
                        <p className="text-white font-bold mt-1" data-testid="part-heat-treatment">
                          {partDetails.heat_treatment || 'Not specified'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-500">
                      <p>Loading part details...</p>
                    </div>
                  )}
                  {partDrawings.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-blue-500/20">
                      <Button
                        onClick={() => {
                          partDrawings.forEach(drawing => {
                            window.open(`${BACKEND_URL}/api/parts/${selectedOrder.part_number}/drawings/${drawing.filepath}`, '_blank');
                          });
                        }}
                        className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/50 font-bold uppercase tracking-wide w-full"
                        data-testid="view-drawings-btn"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        View Engineering Drawings ({partDrawings.length})
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Process Flow Diagram */}
                  <div>
                    <h4 className="text-white font-bold uppercase tracking-wide mb-4 text-sm">
                      Process Flow
                    </h4>
                    <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                      <ProcessFlowDiagram
                        processSequence={selectedOrder.process_sequence}
                        processStatuses={selectedOrder.process_statuses}
                        onProcessClick={handleProcessFlowClick}
                      />
                    </div>
                  </div>

                  {/* Process Details Form */}
                  <div>
                    <h4 className="text-white font-bold uppercase tracking-wide mb-4 text-sm">
                      {selectedProcessForEdit ? `${selectedProcessForEdit} Details` : 'Select a process step'}
                    </h4>
                    <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 max-h-[400px] overflow-y-auto">
                      {selectedProcessForEdit ? (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-800">
                            <span className={`text-xs px-3 py-1 rounded-lg font-bold ${
                              getProcessStatus(selectedOrder, selectedProcessIndex, selectedProcessForEdit) === 'Complete' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                              getProcessStatus(selectedOrder, selectedProcessIndex, selectedProcessForEdit) === 'In Progress' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
                              getProcessStatus(selectedOrder, selectedProcessIndex, selectedProcessForEdit) === 'Ready' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                              'bg-slate-800 text-slate-400 border border-slate-700'
                            }`}>
                              {getProcessStatus(selectedOrder, selectedProcessIndex, selectedProcessForEdit)}
                            </span>
                          </div>

                          {/* Material Sourcing Info Display */}
                          {selectedProcessForEdit === 'Material Sourcing' && partDetails && (
                            <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-4 mb-4" data-testid="material-sourcing-info">
                              <h5 className="text-white font-bold uppercase tracking-wide text-xs mb-3 border-b border-slate-800 pb-2">
                                Material Requirements
                              </h5>
                              <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                  <span className="text-slate-500 text-sm">Material Grade:</span>
                                  <span className="text-white font-mono font-bold" data-testid="material-grade-value">
                                    {partDetails.material_grade || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-slate-500 text-sm">Diameter:</span>
                                  <span className="text-white font-mono font-bold" data-testid="diameter-value">
                                    {partDetails.diameter ? `${partDetails.diameter} mm` : 'N/A'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-slate-500 text-sm">Length of Cut (LOC):</span>
                                  <span className="text-white font-mono font-bold" data-testid="loc-value">
                                    {partDetails.loc ? `${partDetails.loc} mm` : 'N/A'}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t border-slate-800">
                                  <span className="text-slate-500 text-sm">Order Quantity:</span>
                                  <span className="text-white font-mono font-bold">
                                    {selectedOrder.quantity} pcs
                                  </span>
                                </div>
                                <div className="flex justify-between items-center bg-teal-500/10 p-2 rounded-lg border border-teal-500/30">
                                  <span className="text-teal-400 text-sm font-bold">Bars Required:</span>
                                  <span className="text-teal-400 font-mono font-bold text-lg" data-testid="bars-required-value">
                                    {calculateBarsRequired() || 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Heat Treatment Specification from Engineering */}
                          {selectedProcessForEdit === 'Heat Treatment' && partDetails && (
                            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
                              <Label className="text-blue-400 uppercase tracking-wider text-xs mb-2 block">
                                Heat Treatment Specification (from Engineering)
                              </Label>
                              <p className="text-white font-bold text-lg" data-testid="heat-treatment-spec">
                                {partDetails.heat_treatment || 'None specified'}
                              </p>
                            </div>
                          )}

                          {getProcessFields(selectedProcessForEdit).map((field) => (
                            <div key={field.name}>
                              <Label className="text-slate-400 uppercase tracking-wider text-xs mb-2">
                                {field.label}
                              </Label>
                              {field.type === 'select' ? (
                                <Select
                                  value={processData[String(selectedProcessIndex)]?.[field.name] || ''}
                                  onValueChange={(value) => handleProcessDataChange(selectedProcessForEdit, field.name, value)}
                                >
                                  <SelectTrigger className="bg-slate-950/50 border-slate-800">
                                    <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {field.options.map((option) => (
                                      <SelectItem key={option} value={option}>
                                        {option}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  type={field.type}
                                  value={processData[String(selectedProcessIndex)]?.[field.name] || ''}
                                  onChange={(e) => handleProcessDataChange(selectedProcessForEdit, field.name, e.target.value)}
                                  className="bg-slate-950/50 border-slate-800"
                                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                                  data-testid={`${selectedProcessForEdit}-${field.name}-input`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-slate-500">
                          <p className="text-sm">Click on a process step in the flow diagram to edit its details</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4 border-t border-slate-800">
                  <Button
                    onClick={handleSaveProcessData}
                    data-testid="save-process-data-btn"
                    className="bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 border border-teal-500/50 flex-1"
                  >
                    Save Process Data
                  </Button>
                  {(() => {
                    // Check if any step is "In Progress"
                    for (let i = 0; i < selectedOrder.process_sequence.length; i++) {
                      const status = getProcessStatus(selectedOrder, i, selectedOrder.process_sequence[i]);
                      if (status === 'In Progress') return true;
                    }
                    return false;
                  })() && (
                    <Button
                      onClick={handleCompleteCurrentStep}
                      data-testid="complete-step-btn"
                      className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/50 font-bold uppercase tracking-wide px-8"
                    >
                      Complete Step
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="border-slate-700 hover:border-slate-600"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Shortfall Order Dialog */}
        <Dialog open={shortfallDialogOpen} onOpenChange={setShortfallDialogOpen}>
          <DialogContent className="bg-slate-950 border-orange-500/30 max-w-md" data-testid="shortfall-dialog">
            <DialogHeader>
              <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-orange-400" />
                Shortfall Detected
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <p className="text-white mb-2">
                  The final inspection quantity is <span className="font-bold text-orange-400">{shortfallQuantity}</span> units less than the order quantity.
                </p>
                <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                  <div>
                    <span className="text-slate-500">Order Quantity:</span>
                    <p className="text-white font-bold">{pendingShortfallOrder?.quantity}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Inspected Quantity:</span>
                    <p className="text-white font-bold">{pendingShortfallOrder ? pendingShortfallOrder.quantity - shortfallQuantity : 0}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Shortfall:</span>
                    <p className="text-orange-400 font-bold">{shortfallQuantity}</p>
                  </div>
                </div>
              </div>
              
              <p className="text-slate-400 text-sm">
                Would you like to create a new works order for the shortfall quantity?
              </p>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <Button
                variant="outline"
                onClick={handleDeclineShortfallOrder}
                className="border-slate-700 hover:border-slate-600"
                data-testid="decline-shortfall-btn"
              >
                No, Skip
              </Button>
              <Button
                onClick={handleCreateShortfallOrder}
                className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/50 font-bold uppercase tracking-wide"
                data-testid="create-shortfall-btn"
              >
                Yes, Create Shortfall Order
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </main>
    </div>
  );
};

export default Schedule;
